// P14 — Modal genérico "pedir aumento de cota" pro learner.
//
// Reusável: chamado tanto pelo `QuotaDetailsModal` (clicando "Pedir mais")
// quanto pelo `QuotaBlockedModal` (quando estoura 402 tokens). Cria um
// `AgentQuotaIncreaseRequest` PENDING via `quotaService.requestIncrease`
// (endpoint da P08); admin aprova/rejeita no `/admin/agentes/pedidos` (P12).

import { FC, useState } from 'react';
import quotaService from '@/services/quota.service';
import { useToast } from '@/contexts/ToastContext';
import { MyQuota, QuotaIncreaseField } from '@/types/agents';

const MIN_REASON_LEN = 20;

const FIELD_OPTIONS: Array<{ value: QuotaIncreaseField; label: string }> = [
  { value: 'max_tokens_per_day', label: 'Tokens por dia' },
  { value: 'max_tokens_per_month', label: 'Tokens por mês' },
  { value: 'max_personal_agents', label: 'Quantidade de agentes pessoais' },
  { value: 'max_files_per_agent', label: 'Arquivos por agente' },
  { value: 'max_file_size_mb', label: 'Tamanho máximo de arquivo (MB)' },
];

interface Props {
  quota: MyQuota;
  /** Pré-seleciona o campo (útil quando vem do blocked modal). */
  initialField?: QuotaIncreaseField;
  onClose: () => void;
  onSubmitted?: () => void;
}

const QuotaIncreaseRequestModal: FC<Props> = ({ quota, initialField, onClose, onSubmitted }) => {
  const { showToast } = useToast();
  const [field, setField] = useState<QuotaIncreaseField>(initialField ?? 'max_tokens_per_day');
  const [suggested, setSuggested] = useState<string>('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const currentValue = quota[field] as number;
  const reasonOk = reason.trim().length >= MIN_REASON_LEN;

  const handleSubmit = async () => {
    if (!reasonOk) {
      showToast(`Explique melhor o motivo (mínimo ${MIN_REASON_LEN} caracteres).`, 'error');
      return;
    }
    setSubmitting(true);
    try {
      await quotaService.requestIncrease({
        requested_field: field,
        suggested_value: suggested === '' ? undefined : Number(suggested),
        reason: reason.trim(),
      });
      showToast('Pedido enviado! O admin será notificado.', 'success');
      onSubmitted?.();
      onClose();
    } catch {
      showToast('Não foi possível enviar o pedido.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4"
      onClick={onClose}
      data-testid="quota-increase-modal"
    >
      <div
        className="bg-surface-primary border border-surface-border rounded-xl max-w-md w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-bold text-content-primary">Pedir aumento de cota</h2>

        <label className="block">
          <span className="text-sm font-semibold text-content-primary block mb-1">
            Qual limite aumentar?
          </span>
          <select
            value={field}
            onChange={(e) => setField(e.target.value as QuotaIncreaseField)}
            data-testid="qir-field"
            className="w-full rounded-lg bg-surface-secondary border border-surface-border px-3 py-2 text-content-primary text-sm"
          >
            {FIELD_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>

        <div className="text-sm bg-surface-secondary border border-surface-border rounded-lg p-2">
          <span className="text-content-tertiary">Valor atual: </span>
          <strong className="text-content-primary">{currentValue.toLocaleString('pt-BR')}</strong>
        </div>

        <label className="block">
          <span className="text-sm font-semibold text-content-primary block mb-1">
            Sugira novo valor (opcional)
          </span>
          <input
            type="number"
            min={1}
            value={suggested}
            onChange={(e) => setSuggested(e.target.value)}
            data-testid="qir-suggested"
            placeholder="Deixe vazio se o admin decidir"
            className="w-full rounded-lg bg-surface-secondary border border-surface-border px-3 py-2 text-content-primary text-sm"
          />
        </label>

        <label className="block">
          <span className="text-sm font-semibold text-content-primary block mb-1">
            Por que você precisa de mais? <span className="text-content-tertiary">(mín {MIN_REASON_LEN} caracteres)</span>
          </span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="qir-reason"
            rows={4}
            placeholder="Explique como você está usando os agentes e por que precisa de mais cota…"
            className="w-full resize-none rounded-lg bg-surface-secondary border border-surface-border px-3 py-2 text-content-primary text-sm"
          />
          <p
            className={`text-xs mt-1 ${
              reasonOk ? 'text-green-400' : 'text-content-tertiary'
            }`}
          >
            {reason.trim().length}/{MIN_REASON_LEN}+
          </p>
        </label>

        <div className="flex gap-2 justify-end pt-2 border-t border-surface-border">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            data-testid="qir-cancel"
            className="rounded-lg border border-surface-border px-4 py-2 text-sm text-content-primary"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || !reasonOk}
            data-testid="qir-submit"
            className="rounded-lg bg-accent text-surface-primary font-semibold px-4 py-2 text-sm disabled:opacity-50"
          >
            {submitting ? 'Enviando…' : 'Enviar pedido'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuotaIncreaseRequestModal;
